/**
 * Subdomain helpers for account-specific URLs (Atlassian-style tenancy).
 * e.g. acme.localhost:3000, acme.example.com
 *
 * Default PaaS hosts (e.g. *.herokuapp.com) are treated as no tenant subdomain — the first label
 * is the app/service name, not site_name.
 */

/**
 * Parses hostname into subdomain and base (root) host.
 * - "acme.localhost" -> { subdomain: "acme", baseHost: "localhost" }
 * - "localhost" -> { subdomain: null, baseHost: "localhost" }
 * - "acme.myapp.com" -> { subdomain: "acme", baseHost: "myapp.com" }
 * - "myapp.com" -> { subdomain: null, baseHost: "myapp.com" }
 * @param {string} hostname - window.location.hostname
 * @returns {{ subdomain: string | null, baseHost: string }}
 */
export function parseSubdomain(hostname) {
  if (!hostname || typeof hostname !== 'string') {
    return { subdomain: null, baseHost: hostname || '' };
  }
  let parts = hostname.trim().split('.').filter(Boolean);
  if (parts.length === 0) return { subdomain: null, baseHost: hostname };

  // Bare marketing / apex hosts often use www (e.g. www.allthingsdue.com). The first label is not
  // site_name; without this, "www" is treated as tenant, /account/by-site finds nothing, and the
  // nav shows no default "UCS LLC" (App.js falls through to null when tenantSubdomain is set).
  while (parts.length > 2 && parts[0].toLowerCase() === 'www') {
    parts = parts.slice(1);
  }

  const last = parts[parts.length - 1].toLowerCase();
  if (last === 'localhost' || hostname === '127.0.0.1') {
    const baseHost = last === 'localhost' ? 'localhost' : '127.0.0.1';
    const subdomain = parts.length >= 2 ? parts[0].toLowerCase() : null;
    return { subdomain, baseHost };
  }

  // Default PaaS URLs: {stack}.herokuapp.com, {service}.onrender.com, etc. The first label is the
  // deploy name, not tenant site_name. Without this, e.g. ops-mgmt-xxx.herokuapp.com is misread as
  // site_name "ops-mgmt-xxx" and nav branding stays empty instead of the default "UCS LLC".
  if (parts.length === 3) {
    const mid = parts[1].toLowerCase();
    if (mid === 'herokuapp' && last === 'com') {
      return { subdomain: null, baseHost: 'herokuapp.com' };
    }
    if (mid === 'onrender' && last === 'com') {
      return { subdomain: null, baseHost: 'onrender.com' };
    }
    if (mid === 'azurewebsites' && last === 'net') {
      return { subdomain: null, baseHost: 'azurewebsites.net' };
    }
  }

  // Tenant on Heroku: {site_name}.{app}.herokuapp.com (4+ labels). Without this, the generic rule
  // treats subdomain as "ta.ops-mgmt-xxx" and baseHost as "herokuapp.com", breaking /account/by-site
  // and login URL building (site_name must be a single slug like "ta").
  if (parts.length >= 4) {
    const mid = parts[parts.length - 2].toLowerCase();
    if (mid === 'herokuapp' && last === 'com') {
      return {
        subdomain: parts[0].toLowerCase(),
        baseHost: parts.slice(1).join('.'),
      };
    }
  }

  if (parts.length >= 2) {
    const baseHost = parts.slice(-2).join('.');
    const subdomain = parts.length > 2 ? parts.slice(0, -2).join('.').toLowerCase() : null;
    return { subdomain, baseHost };
  }

  return { subdomain: null, baseHost: hostname };
}

/**
 * Current subdomain from window.location.hostname, or null if not on a subdomain.
 * @returns {string | null}
 */
export function getCurrentSubdomain() {
  if (typeof window === 'undefined' || !window.location) return null;
  return parseSubdomain(window.location.hostname).subdomain;
}

/**
 * Builds the account-specific login URL for the given site_name.
 * Uses current window origin (protocol, port) and replaces host with siteName.baseHost.
 * If not in a browser or baseHost is missing, returns path-only /Login.
 * @param {string} siteName - account site_name (subdomain slug)
 * @param {{ protocol?: string, host?: string, port?: string }} [override] - optional override (e.g. from env)
 * @returns {string} Full URL to account login page, e.g. http://acme.localhost:3000/Login
 */
export function getAccountLoginUrl(siteName, override) {
  if (!siteName || typeof siteName !== 'string') {
    return '/Login';
  }
  const slug = siteName.trim().toLowerCase();
  if (!slug) return '/Login';

  const protocol = (override && override.protocol) || (typeof window !== 'undefined' && window.location && window.location.protocol) || 'http:';
  const port = (override && override.port) || (typeof window !== 'undefined' && window.location && window.location.port) || '';
  const defaultHost = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
  const host = (override && override.host) || defaultHost;
  const { baseHost } = parseSubdomain(host);
  if (!baseHost) return '/Login';

  const accountHost = `${slug}.${baseHost}`;
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  return `${protocol}//${accountHost}${portSuffix}/Login`;
}
